import { ChangeEventHandler, FC, FormEventHandler, useCallback, useState } from 'react';

import { isEmpty } from 'remeda';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import css from './ConfigureBalanceDialog.module.css';

import { tournamentsService } from 'v2/api/tournamentsService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { TournamentSelect } from 'v2/components/select/tournamentSelect/TournamentSelect';
import { UserModel } from 'v2/types/UserModel';
import { handleHttpError } from 'v2/utils';

interface ConfigureBalanceDialogProps {
  user: UserModel;
  isOpen: boolean;
  onClose: VoidFunction;
}

export const ConfigureBalanceDialog: FC<ConfigureBalanceDialogProps> = ({ user, isOpen, onClose }) => {
  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const [diamonds, setDiamonds] = useState('');

  const { trigger: triggerReset } = useSWRMutation(
    ['resetTournamentBalance', user.id],
    tournamentsService.resetTournamentBalance,
    {
      onSuccess: () => {
        toast.success('Tournament balance reset');
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerUpdate } = useSWRMutation(
    ['updateTournamentBalance', user.id],
    tournamentsService.updateTournamentBalance,
    {
      onSuccess: () => {
        toast.success('Tournament balance updated');
      },
      onError: handleHttpError,
    }
  );

  const handleChangeTournament = useCallback((tournamentId: string) => {
    setSelectedTournamentId(tournamentId);
  }, []);

  const handleChangeDiamonds: ChangeEventHandler<HTMLInputElement> = (e) => {
    setDiamonds(e.target.value);
  };

  const handleResetBalance = () => {
    triggerReset(selectedTournamentId);
  };

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();

    triggerUpdate({ tournamentId: selectedTournamentId, balance: Number(diamonds) });
  };

  const handleClose = () => {
    onClose();
    setSelectedTournamentId('');
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Configure Balance For {user.userNickname}</DialogTitle>

      <DialogContent className={css.configureBalanceContent}>
        <form className="form" onSubmit={handleSubmit}>
          <TournamentSelect
            value={selectedTournamentId}
            onChange={handleChangeTournament}
            filter={{
              isActive: true,
            }}
          />

          <TextField value={diamonds} onChange={handleChangeDiamonds} label="Amount" type="number" fullWidth />

          <div className={css.configureBalanceFormActions}>
            <Popconfirm
              message={`Are you sure that you want to reset balance for ${user.userNickname}?`}
              onConfirm={handleResetBalance}
              disabled={isEmpty(selectedTournamentId)}
            >
              <Button
                type="button"
                variant="contained"
                color="error"
                size="large"
                disabled={isEmpty(selectedTournamentId)}
                fullWidth
              >
                Reset Balance
              </Button>
            </Popconfirm>

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isEmpty(diamonds) || isEmpty(selectedTournamentId)}
            >
              Pay Out Balance
            </Button>
          </div>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="error" variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
